import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useLocation, useNavigate } from 'react-router-dom';
import FeedbackDialog from '@/components/shared/FeedbackDialog';
import ChangelogDialog from '@/components/shared/ChangelogDialog';
import UpdateAvailableBanner from '@/components/shared/UpdateAvailableBanner';
import MissingHoursDialog from '@/components/shared/MissingHoursDialog';
import MobileTimerBar from '@/components/MobileTimerBar';
import { ChatLauncher } from '@/components/chat/ChatLauncher';
import { useSidebarShortcuts } from '@/hooks/use-sidebar-shortcuts';
import { useReturnFocus } from '@/hooks/use-return-focus';
import { useMenuPermissions, type MenuKey } from '@/hooks/use-menu-permissions';
import { useIsLeadBooker, withLeadBooker } from '@/hooks/use-lead-booker';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useChatUnreadBadge } from '@/hooks/use-chat-unread-badge';
import { hasMinRole } from '@/lib/constants';
import AppSidebar from '@/components/layout/AppSidebar';
import AppHeader from '@/components/layout/AppHeader';
import { NAV_CONFIG, TOP_LEVEL_KEYS, type NavItem } from '@/components/layout/nav-items';
import { GROUP_OWN_PAGE_LABELS } from '@/lib/menu-registry';

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout, isSuperadmin } = useAuth();
  const { activeUser, isImpersonating, setImpersonatedUserId, allUsers } = useImpersonation();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Feedback åpnes fra header-knappen uten DialogTrigger; gi fokus tilbake dit (VIB-2536).
  useReturnFocus(feedbackOpen);
  // Innstillinger-gruppen er ekspandert hvis brukeren står på en sub-rute,
  // eller hvis bruker har klikket åpen i denne sesjonen.
  const [innstillingerOpen, setInnstillingerOpen] = useState(
    location.pathname.startsWith('/innstillinger'),
  );

  useEffect(() => {
    if (location.pathname.startsWith('/innstillinger')) {
      setInnstillingerOpen(true);
    }
  }, [location.pathname]);

  const activeRole = activeUser?.role ?? 'user';
  const { data: menuPerms } = useMenuPermissions();
  const isLeadBooker = useIsLeadBooker();
  const freelancerBank = useFeatureFlag('feature_freelancer_bank');
  // VIB-3022: Chat-rommet erstatter den flytende chatten for rollene flagget er på for.
  const chatRoom = useFeatureFlag('feature_chat_rom') && hasMinRole(activeRole, 'user');
  const chatUnread = useChatUnreadBadge(chatRoom);
  // Fail-open under loading: vi viser navet til perms er hentet for å unngå
  // flash av "ingen tilgang" ved første render. Når data ankommer rerendrer
  // React Query og filtrerer som forventet.
  const canAccess = (menuKey: MenuKey | undefined): boolean => {
    if (!menuKey) return true;
    // VIB-2925: frilanserbanken krever i tillegg flagget.
    if (menuKey === 'frilansere' && !freelancerBank) return false;
    if (menuKey === 'chat' && !chatRoom) return false;
    if (!menuPerms) return true;
    return withLeadBooker(
      menuKey,
      menuPerms.find((p) => p.role === activeRole && p.menu_key === menuKey)?.can_access ?? false,
      isLeadBooker,
    );
  };
  // VIB-721b: Bygg navItems dynamisk fra menu_permissions, sortert på
  // sort_order per aktiv rolle. Faller tilbake til TOP_LEVEL_KEYS-rekkefølgen
  // hvis perms ikke er lastet ennå (fail-open under loading).
  const sortOrderFor = (menuKey: MenuKey): number => {
    if (!menuPerms) return TOP_LEVEL_KEYS.indexOf(menuKey);
    const row = menuPerms.find(
      (p) => p.role === activeRole && p.menu_key === menuKey,
    );
    return row?.sort_order ?? 999;
  };
  const sortMenuKeys = (keys: MenuKey[]): MenuKey[] =>
    [...keys].sort((a, b) => sortOrderFor(a) - sortOrderFor(b));

  // Top-level keys uten children gates rett på canAccess(k). Keys MED children
  // (f.eks. "innstillinger") vises hvis enten selve parent-keyen ELLER minst ett
  // barn er tilgjengelig — slik at vanlige ansatte ser Innstillinger-gruppa med
  // KUN personlige barn (f.eks. Varsler) selv om parent-keyen er false for dem.
  const isGroupVisible = (k: MenuKey): boolean => {
    const childKeys = (NAV_CONFIG[k] as { childKeys?: MenuKey[] } | undefined)?.childKeys;
    if (childKeys && childKeys.length > 0) {
      return canAccess(k) || childKeys.some((ck) => canAccess(ck));
    }
    return canAccess(k);
  };
  const orderedTopLevel = sortMenuKeys(TOP_LEVEL_KEYS).filter(isGroupVisible);
  const filteredNav: NavItem[] = orderedTopLevel.map((key) => {
    const cfg = NAV_CONFIG[key];
    const { childKeys, ...base } = cfg as Omit<NavItem, 'menuKey' | 'children'> & { childKeys?: MenuKey[] };
    const item: NavItem = { ...base, menuKey: key };
    if (key === 'chat') item.badge = chatUnread;
    if (childKeys && childKeys.length > 0) {
      const orderedChildren = sortMenuKeys(childKeys).filter((k) => canAccess(k));
      item.children = orderedChildren.map((ck) => ({
        ...NAV_CONFIG[ck],
        menuKey: ck,
      }));
      // Salgsmuligheter: forelderens egen side (Oppdag og pipe) er også en lenke i gruppen.
      const ownLabel = GROUP_OWN_PAGE_LABELS[key];
      if (ownLabel && canAccess(key)) {
        item.children.unshift({ path: base.path, label: ownLabel, icon: base.icon, menuKey: key });
      }
      // En gruppe med bare forelderens egen side er en vanlig lenke.
      if (item.children.length <= 1 && item.children[0]?.menuKey === key) delete item.children;
    }
    return item;
  });
  // VIB-972: Hjelp er alltid synlig for alle roller (menuKey undefined = ikke RBAC-gated).
  filteredNav.push({ path: '/hjelp', label: 'Hjelp', icon: 'info' });
  const shortcuts = useSidebarShortcuts();

  const isShortcutActive = (shortcutPath: string) => {
    const [path, query] = shortcutPath.split('?');
    if (location.pathname !== path) return false;
    if (!query) return location.search === '';
    const shortcutParams = new URLSearchParams(query);
    const currentParams = new URLSearchParams(location.search);
    for (const [key, value] of shortcutParams) {
      if (currentParams.get(key) !== value) return false;
    }
    return true;
  };

  const handleNavClick = () => setMobileDrawerOpen(false);
  const initials = (user?.name ?? '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* VIB-2536: første fokuserbare element; synlig kun ved tastaturfokus. */}
      <a
        href="#innhold"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('innhold')?.focus();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:ring-2 focus:ring-primary"
      >
        Hopp til innhold
      </a>
      {/* Sidebar (md+) */}
      <AppSidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        filteredNav={filteredNav}
        location={location}
        navigate={navigate}
        innstillingerOpen={innstillingerOpen}
        setInnstillingerOpen={setInnstillingerOpen}
        shortcuts={shortcuts}
        isShortcutActive={isShortcutActive}
      />

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <AppHeader
          mobileDrawerOpen={mobileDrawerOpen}
          setMobileDrawerOpen={setMobileDrawerOpen}
          filteredNav={filteredNav}
          location={location}
          innstillingerOpen={innstillingerOpen}
          setInnstillingerOpen={setInnstillingerOpen}
          handleNavClick={handleNavClick}
          shortcuts={shortcuts}
          isShortcutActive={isShortcutActive}
          setFeedbackOpen={setFeedbackOpen}
          user={user}
          logout={logout}
          isSuperadmin={isSuperadmin}
          activeUser={activeUser}
          isImpersonating={isImpersonating}
          setImpersonatedUserId={setImpersonatedUserId}
          allUsers={allUsers}
          initials={initials}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          {/*
            Målet for «Hopp til innhold». Fokusringen vises bare ved tastatur
            (focus-visible), ikke når noen klikker i innholdet; uten den gir
            hoppet ingen synlig respons (smoke-test av #977).
          */}
          <main
            id="innhold"
            tabIndex={-1}
            className="flex-1 overflow-auto bg-background pb-24 md:pb-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
          >
            {children}
          </main>
        </div>
      </div>

      <MobileTimerBar />
      {!chatRoom && <ChatLauncher />}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      {/* VIB-2621: «Nytt i Revehiet» én gang per bruker, styrer seg selv (flagg + sett-status). */}
      <ChangelogDialog />
      {/* VIB-2834: varsel når en nyere versjon er deployet. Laster aldri inn av seg selv. */}
      <UpdateAvailableBanner />
      {/*
        VIB-2093: påminnelse om dager uten timeføring. Viser seg én gang per
        bruker per dag, så den kan stå ubetinget her.
      */}
      <MissingHoursDialog />
    </div>
  );
}
